import { createIcons, Settings, SlidersHorizontal, Lock, CheckCircle2, AlertTriangle, RefreshCw, ShieldCheck, ShieldAlert, KeyRound, PlugZap, Activity, History, Hash, Workflow, ClipboardCheck, ScanLine, Gauge, FileUp, Bell, FolderOpen, FileSignature, Save, RotateCcw, Plus, Trash2, ExternalLink } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { router } from "../router/router.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { AppError } from "../../shared/errors/app-error.js";
import {
  END_DOMAIN_REQUIREMENTS,
  END_METHODS,
  END_METHOD_LABELS,
  IMAGE_EXTENSION_OPTIONS,
  IMPORT_CONFLICT_POLICIES,
  LOCKED_RULES,
  NOTIFICATION_SETTING_EVENTS,
  PAGE_SIZE_OPTIONS,
  PASSWORD_MIN_LENGTH_FLOOR,
  SECTIONS_REQUIRING_REASON,
  SETTINGS_TABS,
  TIMEZONE_OPTIONS,
  effectOf,
  formatCnpj,
  formatForDisplay,
  isValidCnpj,
  normalizeCnpj,
  passwordPolicyViolations,
  type EndMethod,
  type SettingsSectionId,
  type SystemSettings
} from "../../domain/settings/system-settings.js";
import {
  DEFAULT_NUMBERING,
  NON_CONFIGURABLE_NUMBERING,
  NUMBERED_DOCUMENT_LABELS,
  NUMBERED_DOCUMENT_TYPES,
  NUMBERING_TOKENS,
  buildTemplateFromTokens,
  numberingWarnings,
  previewDocumentNumber,
  validateNumberingConfig,
  type NumberedDocumentType
} from "../../domain/settings/document-numbering.js";
import { importTargetFields } from "../../domain/import/equipment-import.js";
import { loadList, type ContextList } from "../context/context-load.js";
import { clearOperationalSettingsCache } from "../settings/operational-settings.js";
import type { DiagnosticResult, IntegrationView, SettingsView, WorkflowOverview } from "../../application/services/system-settings-service.js";

type EditableDraftSection = Exclude<SettingsSectionId, "workflow" | "integration">;

/** Rótulo legível de cada caminho de configuração (histórico e mensagens). */
const FIELD_LABELS: Record<string, string> = {
  "general.language": "Idioma padrão", "general.timezone": "Fuso horário", "general.dateFormat": "Formato de data", "general.timeFormat": "Formato de hora",
  "general.decimalSeparator": "Separador decimal", "general.thicknessUnit": "Unidade de espessura", "general.pressureUnit": "Unidade de pressão",
  "general.temperatureUnit": "Unidade de temperatura", "general.pageSize": "Registros por página", "general.autosaveEnabled": "Autosave",
  "general.autosaveDelaySeconds": "Espera do autosave (s)", "general.confirmBeforeFinalize": "Confirmar antes de finalizar",
  "inspection.autosaveMode": "Autosave da inspeção", "inspection.autosaveDelaySeconds": "Espera do autosave da inspeção (s)", "inspection.confirmBeforeFinalize": "Confirmar antes de finalizar a inspeção",
  "inspection.draftRecovery": "Recuperação de rascunho", "inspection.defaultNature": "Natureza padrão", "inspection.defaultModality": "Modalidade padrão", "inspection.warnIncomplete": "Avisar preenchimento incompleto",
  "integrity.thicknessDecimals": "Casas decimais — espessura", "integrity.corrosionRateDecimals": "Casas decimais — taxa de corrosão", "integrity.remainingLifeDecimals": "Casas decimais — vida remanescente", "integrity.roundingMode": "Arredondamento de exibição",
  "import.dateFormat": "Formato de data da importação", "import.decimalSeparator": "Separador decimal da importação", "import.extraEmptyMarkers": "Marcadores de vazio adicionais", "import.aliases": "Aliases de colunas", "import.conflictPolicy": "Política de conflito",
  "files.maxImageMb": "Tamanho máximo de imagem (MB)", "files.allowedImageExtensions": "Extensões permitidas", "files.compressionEnabled": "Compressão", "files.maxResolutionPx": "Resolução máxima (px)", "files.imageQuality": "Qualidade de imagem", "files.maxPhotosPerReport": "Limite de fotos por relatório", "files.maxAttachmentsPerReport": "Limite de anexos",
  "security.idleTimeoutMinutes": "Tempo de inatividade (min)", "security.sessionMaxHours": "Tempo máximo de sessão (h)", "security.loginMaxAttempts": "Tentativas de login", "security.lockoutMinutes": "Bloqueio temporário (min)", "security.reauthForCriticalActions": "Reautenticação para ações críticas",
  "security.passwordMinLength": "Senha — tamanho mínimo", "security.passwordRequireUpper": "Senha — exigir maiúscula", "security.passwordRequireLower": "Senha — exigir minúscula", "security.passwordRequireDigit": "Senha — exigir número", "security.passwordRequireSymbol": "Senha — exigir símbolo",
  "signature.secret": "Segredo da ICP (máscara)", "signature.service.status": "Status da integração ICP"
};

function labelOfPath(path: string): string {
  if (FIELD_LABELS[path]) return FIELD_LABELS[path]!;
  const parts = path.split(".");
  if (parts[0] === "numbering" && parts[1]) return `Numeração ${parts[1]} — ${parts.slice(2).join(".")}`;
  if (parts[0] === "end" && parts[1]) return `END ${parts[1]} — ${parts.slice(2).join(".")}`;
  if (parts[0] === "notification") return `Notificação — ${parts.slice(2).join(".")}`;
  if (parts[0] === "signature") return `Assinatura ICP — ${parts.slice(1).join(".")}`;
  return path;
}

/**
 * CONFIGURAÇÕES — central de parametrização FUNCIONAL. Aparência (logo,
 * cores, fonte, tela de login, layout, template visual) é do STUDIO e não
 * aparece aqui.
 *
 * A tela é conveniência: toda gravação passa por `SystemSettingsService`,
 * que confere a permissão de cada ato, valida, grava por versão e registra o
 * histórico. Esconder uma aba não é o controle.
 */
class AppSettingsPage extends HTMLElement {
  private tab = "geral";
  private view: SettingsView | null = null;
  private drafts: Partial<Record<EditableDraftSection, any>> = {};
  private reasons: Partial<Record<string, string>> = {};
  private notice: { kind: "ok" | "error" | "info"; text: string } | null = null;
  private loading = true;
  private loadError = "";
  private saving = false;
  private workflow: WorkflowOverview | null = null;
  private integrations: IntegrationView[] | null = null;
  private diagnosticsInfo: any = null;
  private diagnostics: DiagnosticResult[] = [];
  private icpTest: DiagnosticResult | null = null;
  private history: any[] | null = null;
  private historyFilter = "";
  private secretInput = "";
  private endCatalogs: Partial<Record<EndMethod, { procedures: ContextList<any>; norms: ContextList<any>; criteria: ContextList<any> }>> = {};
  private busy = "";
  private resetPasswordInput = "";
  private resetPhraseInput = "";
  private resetResult: any = null;

  connectedCallback() {
    const params = new URLSearchParams(window.location.search);
    const requested = params.get("tab");
    if (requested && SETTINGS_TABS.some(t => t.id === requested)) this.tab = requested;
    void this.load();
  }

  // ═══════════════════════════════════════════════════════ contexto

  private ctx(): any {
    const c = contextStore.get();
    return { activeClientId: c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
  }

  private can(permission: string): boolean {
    const perms = contextStore.get().permissions as unknown as { has?: (p: string) => boolean } | string[] | undefined;
    if (!perms) return false;
    return typeof (perms as { has?: unknown }).has === "function" ? !!(perms as { has: (p: string) => boolean }).has(permission) : (perms as string[]).includes(permission);
  }

  private get svc(): any {
    return window.app.platform.systemSettingsService;
  }

  private messageOf(e: unknown, fallback: string): string {
    if (e instanceof AppError) return (e as { safeMessage?: string }).safeMessage || e.message || fallback;
    return (e as { message?: string })?.message || fallback;
  }

  private esc(value: unknown): string {
    return String(value ?? "").replace(/[&<>"']/g, ch => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]!));
  }

  /** VERSÃO-05: ambiente é metadado de implantação, nunca a versão — rótulo em pt-BR, separado. */
  private environmentLabel(environment: string): string {
    switch (environment) {
      case "production": return "Produção";
      case "homologation": return "Homologação";
      case "local": return "Local (teste no navegador)";
      default: return "Desenvolvimento";
    }
  }

  /** VERSÃO-05: RC nunca deve se confundir com a versão em si — rótulo próprio, sempre visível aqui (é a tela de diagnóstico técnico). */
  private releaseStatusLabel(status: string): string {
    switch (status) {
      case "RELEASED": return "Lançada";
      case "RC": return "Release Candidate (RC)";
      case "DEPRECATED": return "Descontinuada";
      default: return "Em desenvolvimento";
    }
  }

  // ═══════════════════════════════════════════════════════ carga

  private async load() {
    this.loading = true;
    this.loadError = "";
    this.render();
    try {
      this.view = await this.svc.getSettings(this.ctx());
      this.drafts = {};
      for (const section of Object.keys(this.view!.settings) as EditableDraftSection[]) {
        this.drafts[section] = structuredClone((this.view!.settings as Record<string, unknown>)[section]);
      }
    } catch (e) {
      this.loadError = this.messageOf(e, "Não foi possível ler as configurações.");
    }
    this.loading = false;
    await this.loadTabData();
    this.render();
  }

  private async loadTabData() {
    const ctx = this.ctx();
    try {
      if (this.tab === "workflow" && !this.workflow) this.workflow = await this.svc.getWorkflowOverview(ctx);
      if (this.tab === "integracoes" && this.can(PermissionCode.SettingsIntegrationRead)) this.integrations = await this.svc.listIntegrations(ctx);
      if (this.tab === "diagnostico" && this.can(PermissionCode.SettingsDiagnosticsRead)) this.diagnosticsInfo = await this.svc.getDiagnosticsInfo(ctx);
      if (this.tab === "historico" && this.can(PermissionCode.SettingsAuditRead)) this.history = await this.svc.listHistory(ctx, 500);
      if (this.tab === "end") await this.loadEndCatalogs();
    } catch (e) {
      this.notice = { kind: "error", text: this.messageOf(e, "Não foi possível carregar esta aba.") };
    }
  }

  /** Procedimentos / Normas / Critérios dos cadastros OFICIAIS — Configurações só aponta qual é o padrão. */
  private async loadEndCatalogs() {
    const ctx = this.ctx();
    const end = this.drafts.end as SystemSettings["end"] | undefined;
    await Promise.all(END_METHODS.map(async method => {
      const current = end?.[method];
      const [procedures, norms, criteria] = await Promise.all([
        loadList<any>(`Procedimentos ${method}`, () => window.app.platform.ndtProcedureService.listSelectableForReport(ctx, method, current?.defaultProcedureId ?? null)),
        loadList<any>(`Normas ${method}`, () => window.app.platform.ndtCatalogService.listSelectableForReport(ctx, "REFERENCE_STANDARD", method, current?.defaultNormId ?? null)),
        loadList<any>(`Critérios ${method}`, () => window.app.platform.ndtCatalogService.listSelectableForReport(ctx, "ACCEPTANCE_CRITERIA", method, current?.defaultCriteriaId ?? null))
      ]);
      this.endCatalogs[method] = { procedures, norms, criteria };
    }));
  }

  // ═══════════════════════════════════════════════════════ gravação

  private async save(section: EditableDraftSection) {
    if (!this.view || this.saving) return;
    this.saving = true;
    this.notice = null;
    this.render();
    try {
      this.view = await this.svc.updateSection(this.ctx(), section, this.drafts[section], this.view.version, this.reasons[section] ?? "");
      this.drafts[section] = structuredClone((this.view!.settings as Record<string, unknown>)[section]);
      this.reasons[section] = "";
      clearOperationalSettingsCache();
      this.history = null;
      this.notice = { kind: "ok", text: "Configuração salva. Vale para os próximos atos — documentos já emitidos não mudam." };
    } catch (e) {
      this.notice = { kind: "error", text: this.messageOf(e, "Não foi possível salvar.") };
    }
    this.saving = false;
    this.render();
  }

  private discard(section: EditableDraftSection) {
    if (!this.view) return;
    this.drafts[section] = structuredClone((this.view.settings as Record<string, unknown>)[section]);
    this.notice = { kind: "info", text: "Alterações descartadas." };
    this.render();
  }

  private isDirty(section: EditableDraftSection): boolean {
    if (!this.view) return false;
    return JSON.stringify(this.drafts[section]) !== JSON.stringify((this.view.settings as Record<string, unknown>)[section]);
  }

  private editable(section: SettingsSectionId): boolean {
    return !!this.view?.editable.includes(section);
  }

  private setPath(section: EditableDraftSection, path: string, value: unknown) {
    const draft = this.drafts[section];
    if (!draft) return;
    const parts = path.split(".");
    let target: any = draft;
    for (const p of parts.slice(0, -1)) target = target[p];
    target[parts[parts.length - 1]!] = value;
  }

  private getPath(section: EditableDraftSection, path: string): any {
    let target: any = this.drafts[section];
    for (const p of path.split(".")) target = target?.[p];
    return target;
  }

  // ═══════════════════════════════════════════════════════ render

  private render() {
    const tabs = SETTINGS_TABS.filter(t => this.tabVisible(t.section));
    if (!tabs.some(t => t.id === this.tab) && tabs[0]) this.tab = tabs[0].id;
    this.innerHTML = `
      <style>${this.styles()}</style>
      <div class="st-head">
        <div>
          <h1><i data-lucide="settings"></i> Configurações</h1>
          <p>Como o sistema <b>funciona</b>: regras, numeração, workflow, segurança, integrações e diagnóstico. Logo, cores, fonte, tela de login e templates visuais ficam no <b>STUDIO INSPECT IA</b>.</p>
          ${this.view ? `<p class="st-meta">Versão ${this.view.version}${this.view.updatedAt ? ` · última alteração ${this.esc(new Date(this.view.updatedAt).toLocaleString("pt-BR"))}` : " · ainda nenhuma alteração (valores padrão)"} · modo ${this.view.mode === "SERVIDOR" ? "servidor" : "navegador (teste local)"}</p>` : ""}
        </div>
        <div class="st-head-actions">
          ${this.can(PermissionCode.IdentityRead) ? `<button class="st-btn ghost" id="st-go-permissions"><i data-lucide="external-link"></i> IR PARA PERMISSIONAMENTO</button>` : ""}
        </div>
      </div>
      ${this.view?.mode === "NAVEGADOR" ? `<div class="st-banner info">Teste local (sem servidor): as configurações ficam neste navegador, como todo o restante do modo local. No servidor (homologação/produção) elas ficam no banco e valem para todos.</div>` : ""}
      ${this.notice ? `<div class="st-banner ${this.notice.kind}">${this.esc(this.notice.text)}</div>` : ""}
      ${this.loadError ? `<div class="st-banner error">${this.esc(this.loadError)}</div>` : ""}
      <div class="st-layout">
        <nav class="st-nav">${tabs.map(t => `<button class="st-nav-item ${t.id === this.tab ? "active" : ""}" data-tab="${t.id}"><span class="st-num">${t.number}</span>${this.esc(t.label)}</button>`).join("")}</nav>
        <main class="st-main">${this.loading ? `<div class="st-empty">Carregando configurações…</div>` : this.view ? this.renderTab() : ""}</main>
      </div>`;
    createIcons({ icons: { Settings, SlidersHorizontal, Lock, CheckCircle2, AlertTriangle, RefreshCw, ShieldCheck, ShieldAlert, KeyRound, PlugZap, Activity, History, Hash, Workflow, ClipboardCheck, ScanLine, Gauge, FileUp, Bell, FolderOpen, FileSignature, Save, RotateCcw, Plus, Trash2, ExternalLink }, attrs: { width: "15", height: "15" } });
    this.bind();
  }

  private tabVisible(section: string): boolean {
    if (section === "diagnostics") return this.can(PermissionCode.SettingsDiagnosticsRead);
    if (section === "history") return this.can(PermissionCode.SettingsAuditRead);
    if (section === "danger_zone") return this.can(PermissionCode.SystemDatabaseReset);
    if (!this.view) return section === "general";
    return this.view.readable.includes(section as SettingsSectionId);
  }

  private renderTab(): string {
    switch (this.tab) {
      case "geral": return this.renderGeneral();
      case "numeracao": return this.renderNumbering();
      case "workflow": return this.renderWorkflow();
      case "inspecoes": return this.renderInspection();
      case "end": return this.renderEnd();
      case "integridade": return this.renderIntegrity();
      case "importacoes": return this.renderImport();
      case "notificacoes": return this.renderNotifications();
      case "arquivos": return this.renderFiles();
      case "seguranca": return this.renderSecurity();
      case "assinatura": return this.renderSignature();
      case "integracoes": return this.renderIntegrations();
      case "diagnostico": return this.renderDiagnostics();
      case "historico": return this.renderHistory();
      case "zonaderisco": return this.renderDangerZone();
      default: return "";
    }
  }

  // ─────────────────────────────────────────────────────── blocos comuns

  private effect(section: string, field: string): string {
    const e = effectOf(section, field);
    if (!e) return "";
    return e.applied
      ? `<span class="st-effect ok" title="${this.esc(e.where)}"><i data-lucide="check-circle-2"></i> APLICADO</span>`
      : `<span class="st-effect reg" title="${this.esc(e.where)}"><i data-lucide="alert-triangle"></i> REGISTRADO — AINDA NÃO APLICADO</span>`;
  }

  private effectNote(section: string, field: string): string {
    const e = effectOf(section, field);
    return e ? `<small class="st-note">${this.esc(e.where)}</small>` : "";
  }

  private sectionHead(icon: string, title: string, subtitle: string): string {
    return `<div class="st-section-head"><h2><i data-lucide="${icon}"></i> ${this.esc(title)}</h2><p>${subtitle}</p></div>`;
  }

  private saveBar(section: EditableDraftSection): string {
    // UX Correção: mensagem redundante removida; botões já desabilitados/ocultos via editable()
if (!this.editable(section)) return '';
    const dirty = this.isDirty(section);
    const needsReason = SECTIONS_REQUIRING_REASON.has(section);
    return `
      <div class="st-savebar">
        ${needsReason ? `<label class="st-reason"><span>Motivo da alteração *</span><input class="st-ctl" data-reason="${section}" value="${this.esc(this.reasons[section] ?? "")}" placeholder="Obrigatório nesta seção — fica no histórico"></label>` : `<label class="st-reason"><span>Motivo (opcional)</span><input class="st-ctl" data-reason="${section}" value="${this.esc(this.reasons[section] ?? "")}" placeholder="Fica registrado no histórico"></label>`}
        <div class="st-savebar-actions">
          <button class="st-btn ghost" data-discard="${section}" ${dirty ? "" : "disabled"}><i data-lucide="rotate-ccw"></i> Descartar</button>
          <button class="st-btn primary" data-save="${section}" ${dirty && !this.saving ? "" : "disabled"}><i data-lucide="save"></i> ${this.saving ? "Salvando…" : "Salvar"}</button>
        </div>
      </div>`;
  }

  private ro(section: SettingsSectionId): string {
    return this.editable(section) ? "" : "disabled";
  }

  private selectField(section: EditableDraftSection, path: string, options: ReadonlyArray<{ value: string; label: string; disabled?: boolean }>, kind: "str" | "int" = "str"): string {
    const current = String(this.getPath(section, path) ?? "");
    return `<select class="st-ctl" data-sec="${section}" data-path="${path}" data-kind="${kind}" ${this.ro(section)}>${options.map(o => `<option value="${this.esc(o.value)}" ${o.value === current ? "selected" : ""} ${o.disabled ? "disabled" : ""}>${this.esc(o.label)}</option>`).join("")}</select>`;
  }

  private numberField(section: EditableDraftSection, path: string, attrs: { min: number; max: number; step?: number; nullable?: boolean; placeholder?: string }): string {
    const v = this.getPath(section, path);
    return `<input class="st-ctl" type="number" min="${attrs.min}" max="${attrs.max}" step="${attrs.step ?? 1}" data-sec="${section}" data-path="${path}" data-kind="${attrs.nullable ? "nullnum" : attrs.step && attrs.step < 1 ? "float" : "int"}" value="${v === null || v === undefined ? "" : this.esc(v)}" placeholder="${this.esc(attrs.placeholder ?? "")}" ${this.ro(section)}>`;
  }

  private textField(section: EditableDraftSection, path: string, placeholder = "", extra = ""): string {
    return `<input class="st-ctl" data-sec="${section}" data-path="${path}" data-kind="str" value="${this.esc(this.getPath(section, path) ?? "")}" placeholder="${this.esc(placeholder)}" ${extra} ${this.ro(section)}>`;
  }

  private toggle(section: EditableDraftSection, path: string, label: string, disabled = false): string {
    const v = !!this.getPath(section, path);
    return `<label class="st-toggle"><input type="checkbox" data-sec="${section}" data-path="${path}" data-kind="bool" ${v ? "checked" : ""} ${disabled || !this.editable(section) ? "disabled" : ""}> <span>${this.esc(label)}</span></label>`;
  }

  private row(section: string, field: string, label: string, control: string): string {
    return `<div class="st-row"><div class="st-row-label"><span>${this.esc(label)}</span>${this.effect(section, field)}${this.effectNote(section, field)}</div><div class="st-row-control">${control}</div></div>`;
  }

  private lockedRules(area: string): string {
    const rules = LOCKED_RULES.filter(r => r.area === area);
    if (rules.length === 0) return "";
    return `
      <div class="st-locked">
        <div class="st-locked-head"><i data-lucide="lock"></i> Regras obrigatórias — não desligáveis</div>
        <table class="st-table"><thead><tr><th>Regra</th><th>Valor</th><th>Por que não é parametrização</th></tr></thead>
        <tbody>${rules.map(r => `<tr><td><b>${this.esc(r.label)}</b></td><td><span class="st-tag lock">${this.esc(r.value)}</span></td><td>${this.esc(r.reason)}</td></tr>`).join("")}</tbody></table>
      </div>`;
  }

  // ─────────────────────────────────────────────────────── 01 GERAL

  private renderGeneral(): string {
    const s: EditableDraftSection = "general";
    return `
      ${this.sectionHead("sliders-horizontal", "01 — Geral", "Comportamento global. A marca <b>APLICADO</b> diz onde o valor já é lido; <b>REGISTRADO</b> diz que o valor é guardado, mas nenhum módulo o lê ainda — mudar não altera nada até ser ligado.")}
      <div class="st-card">
        ${this.row(s, "language", "Idioma padrão", this.selectField(s, "language", [{ value: "pt-BR", label: "Português (Brasil)" }]))}
        ${this.row(s, "timezone", "Fuso horário", this.selectField(s, "timezone", TIMEZONE_OPTIONS.map(t => ({ value: t, label: t }))))}
        ${this.row(s, "dateFormat", "Formato de data", this.selectField(s, "dateFormat", [{ value: "DD/MM/AAAA", label: "DD/MM/AAAA" }, { value: "AAAA-MM-DD", label: "AAAA-MM-DD" }]))}
        ${this.row(s, "timeFormat", "Formato de hora", this.selectField(s, "timeFormat", [{ value: "24H", label: "24 horas" }, { value: "12H", label: "12 horas" }]))}
        ${this.row(s, "decimalSeparator", "Separador decimal", this.selectField(s, "decimalSeparator", [{ value: ",", label: "Vírgula (1,25)" }, { value: ".", label: "Ponto (1.25)" }]))}
        ${this.row(s, "thicknessUnit", "Unidade padrão de espessura", this.selectField(s, "thicknessUnit", [{ value: "mm", label: "mm" }, { value: "pol", label: "pol" }]))}
        ${this.row(s, "pressureUnit", "Unidade padrão de pressão", this.selectField(s, "pressureUnit", ["kgf/cm²", "bar", "MPa", "psi"].map(u => ({ value: u, label: u }))))}
        ${this.row(s, "temperatureUnit", "Unidade padrão de temperatura", this.selectField(s, "temperatureUnit", ["°C", "°F"].map(u => ({ value: u, label: u }))))}
        ${this.row(s, "pageSize", "Registros por página", this.selectField(s, "pageSize", PAGE_SIZE_OPTIONS.map(n => ({ value: String(n), label: String(n) })), "int"))}
        ${this.row(s, "autosaveEnabled", "Autosave", this.toggle(s, "autosaveEnabled", "Gravar automaticamente o rascunho"))}
        ${this.row(s, "autosaveDelaySeconds", "Espera do autosave (segundos)", this.numberField(s, "autosaveDelaySeconds", { min: 1, max: 120 }) + `<small class="st-note">Tempo depois da última digitação. Com autosave desligado, o relatório só grava em "Salvar rascunho".</small>`)}
        ${this.row(s, "confirmBeforeFinalize", "Confirmação antes de finalizar documentos", this.toggle(s, "confirmBeforeFinalize", "Pedir confirmação"))}
      </div>
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 02 NUMERAÇÃO

  private renderNumbering(): string {
    const s: EditableDraftSection = "numbering";
    const draft = this.drafts.numbering as SystemSettings["numbering"];
    return `
      ${this.sectionHead("hash", "02 — Numeração documental", "Modelo por tipo de documento. O padrão de cada linha reproduz <b>exatamente</b> o número que o módulo já gerava. O número é atribuído <b>uma única vez</b>, na criação — mudar o modelo não renumera documentos existentes.")}
      <div class="st-card st-tokens"><b>Tokens:</b> ${NUMBERING_TOKENS.map(t => `<code class="st-token ${t.legacy ? "legacy" : ""}" title="${this.esc(t.label)}">${this.esc(t.token)}</code>`).join(" ")}
        <p class="st-note">Sequência segura para vários usuários: incremento atômico no armazenamento (nunca "maior número + 1") e conferência contra os documentos existentes antes de entregar o número.</p></div>
      ${NUMBERED_DOCUMENT_TYPES.map(type => this.renderNumberingRow(type, draft[type])).join("")}
      <div class="st-card">
        <div class="st-card-title">Documentos sem sequência configurável</div>
        <table class="st-table"><thead><tr><th>Documento</th><th>Formato atual</th><th>Motivo</th></tr></thead>
        <tbody>${NON_CONFIGURABLE_NUMBERING.map(n => `<tr><td><b>${this.esc(n.label)}</b></td><td><code>${this.esc(n.currentFormat)}</code></td><td>${this.esc(n.reason)}</td></tr>`).join("")}</tbody></table>
      </div>
      ${this.saveBar(s)}`;
  }

  private renderNumberingRow(type: NumberedDocumentType, cfg: SystemSettings["numbering"][NumberedDocumentType]): string {
    const s: EditableDraftSection = "numbering";
    const errors = validateNumberingConfig({ ...cfg, documentType: type });
    let preview = "";
    if (errors.length === 0) { try { preview = previewDocumentNumber({ ...cfg, documentType: type }); } catch (e) { errors.push((e as Error).message); } }
    const warnings = errors.length === 0 ? numberingWarnings({ ...cfg, documentType: type }) : [];
    const d = DEFAULT_NUMBERING[type];
    const isDefault = cfg.template === d.template && cfg.digits === d.digits && cfg.prefix === d.prefix && cfg.suffix === d.suffix && cfg.resetAnnual === d.resetAnnual && cfg.resetPerClient === d.resetPerClient && cfg.resetPerProject === d.resetPerProject;
    return `
      <div class="st-card">
        <div class="st-card-title">${this.esc(NUMBERED_DOCUMENT_LABELS[type])} ${isDefault ? `<span class="st-tag">PADRÃO</span>` : `<span class="st-tag warn">PERSONALIZADO</span>`} ${this.effect("numbering", "*")}</div>
        <div class="st-grid">
          <label class="st-field wide"><span>Modelo</span>${this.textField(s, `${type}.template`, d.template)}</label>
          <label class="st-field"><span>Dígitos do sequencial</span>${this.numberField(s, `${type}.digits`, { min: 1, max: 10 })}</label>
          <label class="st-field"><span>Prefixo</span>${this.textField(s, `${type}.prefix`)}</label>
          <label class="st-field"><span>Sufixo</span>${this.textField(s, `${type}.suffix`)}</label>
          <label class="st-field"><span>Separador (montador)</span>${this.selectField(s, `${type}.separator`, [{ value: "-", label: "-" }, { value: "_", label: "_" }, { value: ".", label: "." }, { value: "/", label: "/" }, { value: "", label: "(nenhum)" }])}</label>
        </div>
        <div class="st-inline">
          ${this.toggle(s, `${type}.resetAnnual`, "Reinício anual")}
          ${this.toggle(s, `${type}.resetPerClient`, "Contagem por Cliente")}
          ${this.toggle(s, `${type}.resetPerProject`, "Contagem por Projeto")}
          ${this.editable(s) ? `<button class="st-btn sm ghost" data-build-template="${type}" title="Monta {TIPO}, {PROJETO}, {SEQUENCIAL} e {ANO} com o separador escolhido">Montar modelo sugerido</button><button class="st-btn sm ghost" data-reset-numbering="${type}"><i data-lucide="rotate-ccw"></i> Restaurar padrão</button>` : ""}
        </div>
        <div class="st-preview">${errors.length > 0 ? `<span class="st-bad">${errors.map(e => this.esc(e)).join("<br>")}</span>` : `Exemplo: <code>${this.esc(preview)}</code>`}</div>
        ${warnings.map(w => `<div class="st-warnline"><i data-lucide="alert-triangle"></i> ${this.esc(w)}</div>`).join("")}
      </div>`;
  }

  // ─────────────────────────────────────────────────────── 03 WORKFLOW

  private renderWorkflow(): string {
    const w = this.workflow;
    return `
      ${this.sectionHead("workflow", "03 — Workflow e aprovação", "Fluxo documental dos relatórios técnicos. <b>Nenhum parâmetro é editável</b>: tudo o que o comando lista para esta aba (exigir PLH, correção, reenvio, bloqueio do aprovado, snapshot, auditoria, revisão técnica) é regra de governança da política única de aprovação — desligar qualquer um abriria um caminho de aprovação mais fraco.")}
      ${w ? `
        <div class="st-card">
          <div class="st-card-title">Fluxo</div>
          <div class="st-flow">${w.flow.map((f, i) => `<span class="st-flow-step">${this.esc(f.label)}</span>${i < w.flow.length - 1 ? `<span class="st-flow-arrow">→</span>` : ""}`).join("")}</div>
          <p class="st-note">Correção solicitada volta para elaboração; o reenvio leva à reanálise. "Aguardando reanálise" é derivado do pedido de correção, não um estado a mais.</p>
        </div>
        <div class="st-card">
          <div class="st-card-title">Documentos sujeitos ao fluxo</div>
          <table class="st-table"><thead><tr><th>Documento</th><th>Análise do PLH</th><th>Aprovar = finalizar</th></tr></thead>
          <tbody>${w.documentTypes.map(d => `<tr><td><b>${this.esc(d.label)}</b></td><td><span class="st-tag lock">OBRIGATÓRIA</span></td><td>${d.approvalFinalizes ? "Sim — a aprovação já fecha o documento" : "Não — finalização é ato separado"}</td></tr>`).join("")}</tbody></table>
        </div>` : `<div class="st-empty">Carregando fluxo…</div>`}
      ${this.lockedRules("workflow")}
      ${this.lockedRules("security")}`;
  }

  // ─────────────────────────────────────────────────────── 04 INSPEÇÕES

  private renderInspection(): string {
    const s: EditableDraftSection = "inspection";
    return `
      ${this.sectionHead("clipboard-check", "04 — Inspeções", "Padrões da Checklist de inspeção.")}
      <div class="st-card">
        ${this.row(s, "autosaveMode", "Autosave", this.selectField(s, "autosaveMode", [{ value: "HERDAR", label: "Usar o de Geral" }, { value: "LIGADO", label: "Ligado" }, { value: "DESLIGADO", label: "Desligado" }]))}
        ${this.row(s, "autosaveDelaySeconds", "Espera do autosave (segundos)", this.numberField(s, "autosaveDelaySeconds", { min: 1, max: 120, nullable: true, placeholder: "usar o de Geral" }))}
        ${this.row(s, "confirmBeforeFinalize", "Confirmação antes de finalizar", this.selectField(s, "confirmBeforeFinalize", [{ value: "HERDAR", label: "Usar o de Geral" }, { value: "SIM", label: "Sim" }, { value: "NAO", label: "Não" }]))}
        ${this.row(s, "draftRecovery", "Recuperação de rascunho", this.toggle(s, "draftRecovery", "Recuperar rascunho não finalizado"))}
        ${this.row(s, "defaultNature", "Natureza padrão", this.selectField(s, "defaultNature", [{ value: "", label: "Nenhuma (escolher sempre)" }, { value: "INITIAL", label: "Inicial" }, { value: "PERIODIC", label: "Periódica" }]) + `<small class="st-note">Extraordinária não pode ser padrão: exige motivo.</small>`)}
        ${this.row(s, "defaultModality", "Modalidade padrão", this.selectField(s, "defaultModality", [{ value: "", label: "Nenhuma (escolher sempre)" }, { value: "INTERNAL", label: "Interna" }, { value: "EXTERNAL", label: "Externa" }, { value: "GENERAL", label: "Geral" }]))}
        ${this.row(s, "warnIncomplete", "Avisos de preenchimento incompleto", this.toggle(s, "warnIncomplete", "Avisar o que falta"))}
      </div>
      ${this.lockedRules("inspection")}
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 05 END

  private renderEnd(): string {
    const s: EditableDraftSection = "end";
    return `
      ${this.sectionHead("scan-line", "05 — END", "Qual é o PADRÃO de cada método. Procedimentos, normas, critérios, equipamentos e consumíveis continuam nos seus cadastros oficiais — aqui só se aponta qual entra pré-selecionado. Obrigatoriedades só podem ser <b>acrescentadas</b> ao mínimo de cada método.")}
      ${END_METHODS.map(method => this.renderEndMethod(method)).join("")}
      ${this.lockedRules("end")}
      ${this.saveBar(s)}`;
  }

  private renderEndMethod(method: EndMethod): string {
    const s: EditableDraftSection = "end";
    const cat = this.endCatalogs[method];
    const applies = method === "PM" || method === "USC" || method === "BSCAN";
    const catalogSelect = (field: string, list: ContextList<any> | undefined, label: (i: any) => string) => {
      const current = this.getPath(s, `${method}.${field}`) ?? "";
      if (!list) return `<select class="st-ctl" disabled><option>Carregando…</option></select>`;
      if (list.status !== "ok") return `<select class="st-ctl" disabled><option>${this.esc(list.placeholder)}</option></select><small class="st-bad">${this.esc(list.message)}</small>`;
      return `<select class="st-ctl" data-sec="${s}" data-path="${method}.${field}" data-kind="nullstr" ${this.ro(s)}><option value="">Nenhum padrão</option>${list.items.map(i => `<option value="${this.esc(i.id)}" ${i.id === current ? "selected" : ""}>${this.esc(label(i))}</option>`).join("")}</select>`;
    };
    const itemLabel = (i: any) => [i.code, i.name].filter(Boolean).join(" — ") || i.id;
    const procLabel = (p: any) => [p.code, p.title || p.name, p.revision ? `Rev. ${p.revision}` : ""].filter(Boolean).join(" — ") || p.id;
    return `
      <div class="st-card">
        <div class="st-card-title">${this.esc(END_METHOD_LABELS[method])} (${method}) ${applies ? this.effect("end", "defaultProcedureId") : `<span class="st-effect reg" title="O Relatório ${method} tem tela própria e ainda não lê estes padrões."><i data-lucide="alert-triangle"></i> REGISTRADO — AINDA NÃO APLICADO NO ${method}</span>`}</div>
        <div class="st-grid">
          <label class="st-field"><span>Procedimento padrão</span>${catalogSelect("defaultProcedureId", cat?.procedures, procLabel)}</label>
          <label class="st-field"><span>Norma padrão</span>${catalogSelect("defaultNormId", cat?.norms, itemLabel)}</label>
          <label class="st-field"><span>Critério padrão</span>${catalogSelect("defaultCriteriaId", cat?.criteria, itemLabel)}</label>
        </div>
        <div class="st-inline">
          ${this.toggle(s, `${method}.requirePhoto`, "Exigir ao menos uma foto")}
          ${this.toggle(s, `${method}.requireCroqui`, "Exigir croqui")}
        </div>
        <div class="st-domain-req"><i data-lucide="lock"></i> Já exigido pelo método (não removível): ${END_DOMAIN_REQUIREMENTS[method].map(r => this.esc(r)).join(" · ")}</div>
      </div>`;
  }

  // ─────────────────────────────────────────────────────── 06 INTEGRIDADE

  private renderIntegrity(): string {
    const s: EditableDraftSection = "integrity";
    const d = this.drafts.integrity as SystemSettings["integrity"];
    return `
      ${this.sectionHead("gauge", "06 — Integridade / AVR", "Apresentação dos resultados. O arredondamento é <b>só de exibição</b>: o cálculo sempre usa a precisão completa.")}
      <div class="st-card">
        ${this.row(s, "thicknessDecimals", "Casas decimais — espessura", this.numberField(s, "thicknessDecimals", { min: 0, max: 4 }))}
        ${this.row(s, "corrosionRateDecimals", "Casas decimais — taxa de corrosão", this.numberField(s, "corrosionRateDecimals", { min: 1, max: 6 }))}
        ${this.row(s, "remainingLifeDecimals", "Casas decimais — vida remanescente", this.numberField(s, "remainingLifeDecimals", { min: 0, max: 3 }))}
        ${this.row(s, "roundingMode", "Arredondamento de exibição", this.selectField(s, "roundingMode", [{ value: "HALF_UP", label: "Metade para cima (0,2535 → 0,254)" }, { value: "HALF_EVEN", label: "Metade para o par (bancário)" }]))}
        <div class="st-row"><div class="st-row-label"><span>Unidades</span><small class="st-note">Fixas pelo motor de cálculo API 570.</small></div><div class="st-row-control"><span class="st-tag lock">mm/ano</span> <span class="st-tag lock">anos</span></div></div>
      </div>
      <div class="st-card st-example">
        <div class="st-card-title">Exemplo</div>
        <p>Taxa calculada internamente: <code>0,253784</code> → exibida: <b>${this.esc(formatForDisplay(0.253784, d.corrosionRateDecimals, d.roundingMode))} mm/ano</b>. O valor gravado e usado nos cálculos continua 0,253784.</p>
      </div>
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 07 IMPORTAÇÕES

  private renderImport(): string {
    const s: EditableDraftSection = "import";
    const d = this.drafts.import as SystemSettings["import"];
    const fields = importTargetFields();
    return `
      ${this.sectionHead("file-up", "07 — Importações", "Lista de Linhas / Sistemas. Aliases são <b>explícitos</b>: o título da planilha precisa casar exatamente (sem acento, sem caixa) — não há aproximação.")}
      <div class="st-card">
        ${this.row(s, "dateFormat", "Formato de data", this.selectField(s, "dateFormat", [{ value: "DD/MM/AAAA", label: "DD/MM/AAAA" }, { value: "AAAA-MM-DD", label: "AAAA-MM-DD" }]))}
        ${this.row(s, "decimalSeparator", "Separador decimal", this.selectField(s, "decimalSeparator", [{ value: "AUTO", label: "Automático (aceita , e .)" }, { value: ",", label: "Vírgula" }, { value: ".", label: "Ponto" }]))}
        ${this.row(s, "extraEmptyMarkers", "Marcadores de vazio adicionais", `<input class="st-ctl" data-sec="${s}" data-path="extraEmptyMarkers" data-kind="list" value="${this.esc(d.extraEmptyMarkers.join("; "))}" placeholder="Ex.: A DEFINIR; S/I" ${this.ro(s)}><small class="st-note">Separados por ponto e vírgula. Somam-se aos que já valem: NÃO INFORMADO, N/A, N/D, SEM INFORMAÇÃO, -.</small>`)}
      </div>
      <div class="st-card">
        <div class="st-card-title">Política de conflito ${this.effect("import", "conflictPolicy")}</div>
        ${IMPORT_CONFLICT_POLICIES.map(p => `<label class="st-radio ${p.available ? "" : "off"}"><input type="radio" name="st-conflict" data-sec="${s}" data-path="conflictPolicy" data-kind="str" value="${p.value}" ${d.conflictPolicy === p.value ? "checked" : ""} ${p.available && this.editable(s) ? "" : "disabled"}> <b>${this.esc(p.label)}</b>${p.available ? " (recomendado)" : ""}<small>${this.esc(p.reason)}</small></label>`).join("")}
      </div>
      <div class="st-card">
        <div class="st-card-title">Aliases de colunas ${this.effect("import", "aliases")}</div>
        <table class="st-table"><thead><tr><th>Título na planilha</th><th>Campo do cadastro</th><th></th></tr></thead>
        <tbody>${d.aliases.length === 0 ? `<tr><td colspan="3" class="st-muted">Nenhum alias adicional. Os títulos já reconhecidos continuam valendo.</td></tr>` : d.aliases.map((a, i) => `
          <tr>
            <td><input class="st-ctl" data-alias-index="${i}" data-alias-part="alias" value="${this.esc(a.alias)}" placeholder="PRESSÃO DE PROJETO" ${this.ro(s)}></td>
            <td><select class="st-ctl" data-alias-index="${i}" data-alias-part="field" ${this.ro(s)}>${fields.map(f => `<option value="${this.esc(f.field)}" ${f.field === a.field ? "selected" : ""}>${this.esc(f.label)} (${this.esc(f.field)})</option>`).join("")}</select></td>
            <td>${this.editable(s) ? `<button class="st-icon" data-alias-remove="${i}" title="Remover"><i data-lucide="trash-2"></i></button>` : ""}</td>
          </tr>`).join("")}</tbody></table>
        ${this.editable(s) ? `<button class="st-btn sm ghost" id="st-alias-add"><i data-lucide="plus"></i> Adicionar alias</button>` : ""}
      </div>
      ${this.lockedRules("import")}
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 08 NOTIFICAÇÕES

  private renderNotifications(): string {
    const s: EditableDraftSection = "notification";
    return `
      ${this.sectionHead("bell", "08 — Notificações", "Quais eventos geram notificação interna. Eventos que nenhuma rotina do sistema emite hoje aparecem travados, com o motivo. Alerta do sistema não se desliga.")}
      <div class="st-card">
        <table class="st-table"><thead><tr><th>Evento</th><th>Interna ${this.effect("notification", "internal")}</th><th>E-mail</th><th>Observação</th></tr></thead>
        <tbody>${NOTIFICATION_SETTING_EVENTS.map(ev => `
          <tr class="${ev.emitted ? "" : "st-off"}">
            <td><b>${this.esc(ev.label)}</b></td>
            <td>${this.toggle(s, `internal.${ev.id}`, ev.emitted ? "Ligada" : "Sem emissor", !ev.emitted)}</td>
            <td><span class="st-tag muted" title="Não há serviço de e-mail integrado">NÃO INTEGRADO</span></td>
            <td class="st-muted">${this.esc(ev.note)}</td>
          </tr>`).join("")}</tbody></table>
      </div>
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 09 ARQUIVOS

  private renderFiles(): string {
    const s: EditableDraftSection = "files";
    const d = this.drafts.files as SystemSettings["files"];
    return `
      ${this.sectionHead("folder-open", "09 — Arquivos", "Limites de anexos. Valem para o anexo NOVO — um relatório que já tinha mais fotos ou imagens maiores não perde nada.")}
      <div class="st-card">
        ${this.row(s, "maxImageMb", "Tamanho máximo por imagem (MB)", this.numberField(s, "maxImageMb", { min: 0.5, max: 10, step: 0.5 }) + (d.maxImageMb > 4 && this.view?.mode === "NAVEGADOR" ? `<small class="st-bad">No teste local o armazenamento do navegador é pequeno: imagens acima de 4 MB podem não caber.</small>` : ""))}
        ${this.row(s, "allowedImageExtensions", "Extensões permitidas", `<div class="st-inline">${IMAGE_EXTENSION_OPTIONS.map(ext => `<label class="st-toggle"><input type="checkbox" data-ext="${ext}" ${d.allowedImageExtensions.includes(ext) ? "checked" : ""} ${this.ro(s)}> <span>${ext.toUpperCase()}</span></label>`).join("")}</div>`)}
        ${this.row(s, "compressionEnabled", "Compressão", this.toggle(s, "compressionEnabled", "Comprimir imagens"))}
        ${this.row(s, "maxResolutionPx", "Resolução máxima (px)", this.numberField(s, "maxResolutionPx", { min: 640, max: 8192 }))}
        ${this.row(s, "imageQuality", "Qualidade de imagem (40–100)", this.numberField(s, "imageQuality", { min: 40, max: 100 }))}
        ${this.row(s, "maxPhotosPerReport", "Limite de fotos por relatório", this.numberField(s, "maxPhotosPerReport", { min: 1, max: 500, nullable: true, placeholder: "sem limite" }))}
        ${this.row(s, "maxAttachmentsPerReport", "Limite de anexos por relatório", this.numberField(s, "maxAttachmentsPerReport", { min: 1, max: 500, nullable: true, placeholder: "sem limite" }))}
      </div>
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 10 SEGURANÇA

  private renderSecurity(): string {
    const s: EditableDraftSection = "security";
    const d = this.drafts.security as SystemSettings["security"];
    const sample = passwordPolicyViolations(d, "Inspect2026");
    return `
      ${this.sectionHead("shield-check", "10 — Segurança", "Parâmetros de sessão e de senha. Esta tela nunca exibe senha, hash, token ou segredo.")}
      <div class="st-card">
        ${this.row(s, "idleTimeoutMinutes", "Tempo de inatividade (min)", this.numberField(s, "idleTimeoutMinutes", { min: 5, max: 480 }))}
        ${this.row(s, "sessionMaxHours", "Tempo máximo de sessão (h)", this.numberField(s, "sessionMaxHours", { min: 1, max: 24 }))}
        ${this.row(s, "loginMaxAttempts", "Tentativas de login", this.numberField(s, "loginMaxAttempts", { min: 3, max: 20 }))}
        ${this.row(s, "lockoutMinutes", "Bloqueio temporário (min)", this.numberField(s, "lockoutMinutes", { min: 1, max: 1440 }))}
        ${this.row(s, "reauthForCriticalActions", "Reautenticação para ações críticas", this.toggle(s, "reauthForCriticalActions", "Exigir"))}
      </div>
      <div class="st-card">
        <div class="st-card-title">Política de senha ${this.effect("security", "passwordMinLength")}</div>
        ${this.row(s, "passwordMinLength", `Tamanho mínimo (nunca abaixo de ${PASSWORD_MIN_LENGTH_FLOOR})`, this.numberField(s, "passwordMinLength", { min: PASSWORD_MIN_LENGTH_FLOOR, max: 64 }))}
        <div class="st-inline">
          ${this.toggle(s, "passwordRequireUpper", "Exigir maiúscula")}
          ${this.toggle(s, "passwordRequireLower", "Exigir minúscula")}
          ${this.toggle(s, "passwordRequireDigit", "Exigir número")}
          ${this.toggle(s, "passwordRequireSymbol", "Exigir símbolo")}
        </div>
        <p class="st-note">Conferida no serviço ao criar conta com senha, trocar a senha e redefinir pelo Administrador. Contas existentes não são obrigadas a trocar. Exemplo: "Inspect2026" ${sample.length === 0 ? "seria aceita" : `seria recusada — precisa ${this.esc(sample.join(", "))}`}.</p>
      </div>
      ${this.lockedRules("security")}
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 11 ASSINATURA ICP

  private renderSignature(): string {
    const s: EditableDraftSection = "signature";
    const d = this.drafts.signature as SystemSettings["signature"];
    const cnpj = normalizeCnpj(d.company.cnpj);
    const store = this.view!.secretStore;
    const canUpdate = this.editable(s);
    return `
      ${this.sectionHead("file-signature", "11 — Assinatura digital / ICP", "Área funcional, não visual. A integração pertence à <b>empresa</b> (CNPJ) — nunca a uma pessoa (CPF).")}
      <div class="st-banner info">${this.effect("signature", "*")} Nenhum provedor ICP está integrado nesta versão. A configuração é validada e guardada; os documentos continuam assinados pela imagem cadastrada de cada Profissional.</div>
      <div class="st-card">
        <div class="st-card-title">Empresa titular da ICP</div>
        <div class="st-grid">
          <label class="st-field wide"><span>Razão social</span>${this.textField(s, "company.legalName", "Razão social da empresa titular")}</label>
          <label class="st-field"><span>Nome fantasia</span>${this.textField(s, "company.tradeName", "Nome fantasia")}</label>
          <label class="st-field"><span>CNPJ</span><input class="st-ctl" data-sec="${s}" data-path="company.cnpj" data-kind="cnpj" value="${this.esc(cnpj.length === 14 ? formatCnpj(cnpj) : cnpj)}" placeholder="XX.XXX.XXX/0001-XX" ${this.ro(s)}>
            <small class="${cnpj ? (isValidCnpj(cnpj) ? "st-good" : "st-bad") : "st-note"}">${cnpj ? (isValidCnpj(cnpj) ? "CNPJ válido — gravado só com os dígitos." : "CNPJ inválido.") : "Gravado só com os dígitos; a máscara é da tela."}</small></label>
        </div>
      </div>
      <div class="st-card">
        <div class="st-card-title">Serviço ICP</div>
        <div class="st-grid">
          <label class="st-field"><span>Nome da integração</span>${this.textField(s, "service.name")}</label>
          <label class="st-field"><span>Provedor</span>${this.textField(s, "service.provider")}</label>
          <label class="st-field"><span>Ambiente</span>${this.selectField(s, "service.environment", [{ value: "HOMOLOGACAO", label: "Homologação" }, { value: "PRODUCAO", label: "Produção" }])}</label>
          <label class="st-field wide"><span>Endpoint (https)</span>${this.textField(s, "service.endpoint", "https://")}</label>
          <label class="st-field"><span>Client ID</span>${this.textField(s, "service.clientId", "", 'autocomplete="off"')}</label>
          <label class="st-field"><span>Certificado / identidade empresarial</span>${this.textField(s, "service.certificateRef", "referência (não o arquivo)")}</label>
          <label class="st-field"><span>Formato</span>${this.selectField(s, "service.format", [{ value: "PADES", label: "PAdES" }])}</label>
          <label class="st-field"><span>Status</span>${this.selectField(s, "service.status", [{ value: "INATIVO", label: "Inativo" }, { value: "ATIVO", label: "Ativo" }])}</label>
        </div>
        <div class="st-inline">${this.toggle(s, "service.timestamp", "Carimbo de tempo")}${this.toggle(s, "service.postValidation", "Validação pós-assinatura")}</div>
      </div>
      <div class="st-card">
        <div class="st-card-title"><i data-lucide="key-round"></i> Segredo (client secret)</div>
        <p>Atual: <code>${this.esc(d.secretMask ?? "não cadastrado")}</code>${d.secretUpdatedAt ? ` · trocado em ${this.esc(new Date(d.secretUpdatedAt).toLocaleString("pt-BR"))}` : ""}</p>
        ${store.available ? "" : `<div class="st-banner error">${this.esc(store.reason)}</div>`}
        ${canUpdate && store.available ? `
          <div class="st-grid">
            <label class="st-field wide"><span>Novo segredo — vai direto para o cofre do servidor e nunca volta para a tela</span><input class="st-ctl" type="password" id="st-secret" autocomplete="new-password" value="${this.esc(this.secretInput)}"></label>
          </div>
          <div class="st-inline">
            <button class="st-btn" id="st-secret-save" ${this.secretInput.trim().length >= 8 && (this.reasons["signature"] ?? "").trim().length >= 5 ? "" : "disabled"}>TROCAR SEGREDO</button>
            ${d.secretMask ? `<button class="st-btn ghost" id="st-secret-remove" ${(this.reasons["signature"] ?? "").trim().length >= 5 ? "" : "disabled"}>REMOVER SEGREDO</button>` : ""}
            <small class="st-note">Exige o motivo preenchido abaixo.</small>
          </div>` : ""}
      </div>
      <div class="st-card">
        <div class="st-card-title">Testar conexão</div>
        ${canUpdate ? `<button class="st-btn" id="st-icp-test" ${this.busy ? "disabled" : ""}><i data-lucide="plug-zap"></i> TESTAR CONEXÃO</button>` : ""}
        ${this.renderCheckResult(this.icpTest ?? (d.lastTest ? { id: "ICP", label: "Último teste", ok: d.lastTest.ok, status: d.lastTest.status, message: `${d.lastTest.message} (${new Date(d.lastTest.at).toLocaleString("pt-BR")})`, checks: [] } : null))}
      </div>
      <div class="st-card st-muted-card">
        <b>CNPJ × Profissional × PLH</b> — o CNPJ é a titularidade organizacional da integração; o Profissional é a responsabilidade técnica; o PLH é o Profissional nomeado pelo Cliente. A ICP empresarial não substitui o PLH.
      </div>
      ${this.saveBar(s)}`;
  }

  private renderCheckResult(r: DiagnosticResult | null): string {
    if (!r) return "";
    const cls = r.ok ? "ok" : /NÃO|VÁLIDA/.test(r.status) ? "warn" : "bad";
    return `
      <div class="st-result ${cls}">
        <div><b>${this.esc(r.label)}:</b> <span class="st-tag ${cls}">${this.esc(r.status)}</span></div>
        <p>${this.esc(r.message)}</p>
        ${r.checks.length > 0 ? `<table class="st-table"><tbody>${r.checks.map(c => `<tr><td>${this.esc(c.label)}</td><td><span class="st-tag ${c.status === "OK" ? "ok" : c.status === "FALHA" ? "bad" : "warn"}">${this.esc(c.status)}</span></td><td class="st-muted">${this.esc(c.detail)}</td></tr>`).join("")}</tbody></table>` : ""}
      </div>`;
  }

  // ─────────────────────────────────────────────────────── 12 INTEGRAÇÕES

  private renderIntegrations(): string {
    const list = this.integrations;
    const canUpdate = this.can(PermissionCode.SettingsIntegrationUpdate);
    return `
      ${this.sectionHead("plug-zap", "12 — Integrações", "Serviços reais do sistema e o seu estado. Integração que não existe aparece como NÃO INTEGRADO — não há serviço fictício nesta lista.")}
      ${canUpdate ? `<div class="st-card"><label class="st-reason"><span>Motivo (para ativar/desativar)</span><input class="st-ctl" data-reason="integration" value="${this.esc(this.reasons["integration"] ?? "")}"></label></div>` : ""}
      ${!list ? `<div class="st-empty">Carregando integrações…</div>` : `
        <div class="st-card">
          <table class="st-table"><thead><tr><th>Nome</th><th>Tipo</th><th>Status</th><th>Ambiente</th><th>Último teste</th><th>Ações</th></tr></thead>
          <tbody>${list.map(i => `
            <tr>
              <td><b>${this.esc(i.name)}</b><small class="st-note">${this.esc(i.note)}</small></td>
              <td>${this.esc(i.type)}</td>
              <td><span class="st-tag ${i.status === "ATIVO" ? "ok" : i.status === "NÃO INTEGRADO" ? "muted" : "warn"}">${this.esc(i.status)}</span></td>
              <td>${this.esc(i.environment)}</td>
              <td>${i.lastTest ? `<span class="st-tag ${i.lastTest.ok ? "ok" : "warn"}">${this.esc(i.lastTest.status)}</span><small class="st-note">${this.esc(new Date(i.lastTest.at).toLocaleString("pt-BR"))}</small>` : "—"}</td>
              <td class="st-actions">
                ${canUpdate ? `<button class="st-btn sm" data-int-test="${i.id}" ${this.busy ? "disabled" : ""}>TESTAR CONEXÃO</button>` : ""}
                ${i.editTab ? `<button class="st-btn sm ghost" data-goto-tab="${i.editTab}">EDITAR</button>` : ""}
                ${canUpdate && i.id === "ICP" ? (i.status === "ATIVO" ? `<button class="st-btn sm ghost" data-int-toggle="off">DESATIVAR</button>` : `<button class="st-btn sm ghost" data-int-toggle="on">ATIVAR</button>`) : ""}
              </td>
            </tr>`).join("")}</tbody></table>
        </div>`}
      ${this.diagnostics.filter(d => d.label.startsWith("Integração")).map(d => this.renderCheckResult(d)).join("")}`;
  }

  // ─────────────────────────────────────────────────────── 13 DIAGNÓSTICO

  private renderDiagnostics(): string {
    const info = this.diagnosticsInfo;
    const canRun = this.can(PermissionCode.SettingsDiagnosticsExecute);
    return `
      ${this.sectionHead("activity", "13 — Diagnóstico", "Estado da instalação e testes seguros. Nenhuma ação aqui apaga dado nem aplica/desfaz migration.")}
      ${!info ? `<div class="st-empty">Carregando diagnóstico…</div>` : `
        <div class="st-card">
          <div class="st-grid">
            <div class="st-kv"><span>Nome do produto</span><b>${this.esc(info.versionInfo.productName)}</b></div>
            <div class="st-kv"><span>Versão</span><b>${this.esc(info.versionInfo.displayVersion)}</b><small>${this.esc(info.versionInfo.version)} · build ${this.esc(info.versionInfo.buildNumber)}</small></div>
            <div class="st-kv"><span>Ambiente</span><b>${this.esc(this.environmentLabel(info.versionInfo.environment))}</b></div>
            <div class="st-kv"><span>Status de release</span><b>${this.esc(this.releaseStatusLabel(info.versionInfo.releaseStatus))}</b></div>
            <div class="st-kv"><span>Data do release</span><b>${this.esc(info.versionInfo.releaseDate)}</b></div>
            <div class="st-kv"><span>Modo</span><b>${this.esc(info.mode)}</b></div>
            <div class="st-kv"><span>Banco</span><b>${this.esc(info.database.engine)}</b><small>${this.esc(info.database.detail)}</small></div>
            <div class="st-kv"><span>Migration atual</span><b>${this.esc(info.database.currentMigration ?? "—")}</b></div>
            <div class="st-kv"><span>Migrations pendentes</span><b>${info.database.pendingMigrations === null ? "—" : info.database.pendingMigrations.length === 0 ? "Nenhuma" : this.esc(info.database.pendingMigrations.join(", "))}</b></div>
          </div>
          <p class="st-note">${this.esc(info.database.note)}</p>
          <table class="st-table"><thead><tr><th>Serviço</th><th>Estado</th><th>Detalhe</th></tr></thead>
          <tbody>${info.services.map((c: any) => `<tr><td><b>${this.esc(c.label)}</b></td><td><span class="st-tag ${c.status === "OK" ? "ok" : c.status === "FALHA" ? "bad" : "warn"}">${this.esc(c.status)}</span></td><td class="st-muted">${this.esc(c.detail)}</td></tr>`).join("")}</tbody></table>
        </div>`}
      ${canRun ? `
        <div class="st-card st-actions-card">
          <button class="st-btn primary" data-diag="ALL" ${this.busy ? "disabled" : ""}>EXECUTAR DIAGNÓSTICO</button>
          <button class="st-btn" data-diag="PDF_LOCAL" ${this.busy ? "disabled" : ""}>TESTAR PDF</button>
          <button class="st-btn" data-diag="PERMISSION" ${this.busy ? "disabled" : ""}>TESTAR PERMISSION SERVICE</button>
          <button class="st-btn" data-diag="STORAGE" ${this.busy ? "disabled" : ""}>TESTAR ARMAZENAMENTO</button>
          <button class="st-btn" data-diag="ICP" ${this.busy ? "disabled" : ""}>TESTAR ICP</button>
          <button class="st-btn ghost" data-diag="RELOAD" ${this.busy ? "disabled" : ""}><i data-lucide="refresh-cw"></i> RECARREGAR CONFIGURAÇÕES</button>
          <button class="st-btn ghost" data-diag="CACHE" ${this.busy ? "disabled" : ""}>LIMPAR CACHE</button>
        </div>` : `<div class="st-savebar readonly"><i data-lucide="lock"></i> Executar testes exige a permissão settings.diagnostics.execute.</div>`}
      ${this.busy ? `<div class="st-empty">${this.esc(this.busy)}</div>` : ""}
      ${this.diagnostics.map(d => this.renderCheckResult(d)).join("")}`;
  }

  // ─────────────────────────────────────────────────────── 14 HISTÓRICO

  private renderHistory(): string {
    const all = this.history;
    const sections = [...new Set((all ?? []).map(h => h.section))];
    const rows = (all ?? []).filter(h => !this.historyFilter || h.section === this.historyFilter);
    return `
      ${this.sectionHead("history", "14 — Histórico", "Toda alteração de configuração, com o valor anterior e o novo. O histórico é só de acréscimo: nada aqui é editado ou apagado.")}
      ${!all ? `<div class="st-empty">Carregando histórico…</div>` : `
        <div class="st-card">
          <div class="st-inline"><label class="st-field"><span>Seção</span><select class="st-ctl" id="st-history-filter"><option value="">Todas</option>${sections.map(s => `<option value="${this.esc(s)}" ${s === this.historyFilter ? "selected" : ""}>${this.esc(s)}</option>`).join("")}</select></label></div>
          <table class="st-table"><thead><tr><th>Data/Hora</th><th>Usuário</th><th>Configuração</th><th>Valor anterior</th><th>Novo valor</th><th>Motivo</th><th>Status</th></tr></thead>
          <tbody>${rows.length === 0 ? `<tr><td colspan="7" class="st-muted">Nenhuma alteração registrada.</td></tr>` : rows.map(h => `
            <tr>
              <td>${this.esc(new Date(h.at).toLocaleString("pt-BR"))}</td>
              <td>${this.esc(h.userName)}</td>
              <td><b>${this.esc(labelOfPath(h.path))}</b><small class="st-note">${this.esc(h.auditEvent)}</small></td>
              <td><code class="st-val">${this.esc(h.previous)}</code></td>
              <td><code class="st-val">${this.esc(h.next)}</code></td>
              <td>${this.esc(h.reason)}</td>
              <td><span class="st-tag ${h.status === "VIGENTE" ? "ok" : "muted"}">${this.esc(h.status)}</span></td>
            </tr>`).join("")}</tbody></table>
        </div>`}`;
  }

  // ─────────────────────────────────────────────────────── 15 ZONA DE RISCO
  private renderDangerZone(): string {
    const canReset = this.can(PermissionCode.SystemDatabaseReset);
    if (!canReset) {
      return `
        ${this.sectionHead("shield-alert", "15 — Zona de Risco", "Reset controlado e irreversível da base operacional.")}
        <div class="st-savebar readonly"><i data-lucide="lock"></i> Acesso restrito a Administrador autorizado (permissão system.database.reset).</div>`;
    }

    const isPhraseValid = this.resetPhraseInput.trim().toUpperCase() === "EXCLUIR TODOS OS DADOS";
    const isPasswordFilled = this.resetPasswordInput.trim().length > 0;
    const canExecute = isPhraseValid && isPasswordFilled && !this.saving;

    return `
      ${this.sectionHead("shield-alert", "15 — Zona de Risco / Reset da Base", "Zera todos os cadastros e relatórios operacionais com backup prévio automático e reautenticação obrigatória.")}

      <div class="st-card" style="border-color: rgba(239,68,68,0.4); background: rgba(239,68,68,0.04);">
        <div class="st-card-title" style="color: var(--ia-danger-fg, #fca5a5);"><i data-lucide="shield-alert"></i> ATENÇÃO: OPERAÇÃO DESTRUTIVA E IRREVERSÍVEL</div>
        <p style="margin: 0; font-size: 0.8rem; color: var(--ia-fg-soft, #A7B0C0); line-height: 1.5;">
          Esta operação apaga todos os <strong>Clientes, Projetos, Unidades, OS, Sub-OS, Equipamentos, Inspeções, Checklists, Relatórios de Integridade (RI), Relatórios END (ME, LP, PM, USC, B-Scan), AVRs, RDOs, Recomendações e Fotos</strong>.
          <br><br>
          <strong>Garantias estruturais preservadas:</strong>
          <br>
          • O usuário <strong>Administrador</strong> não será excluído.
          <br>
          • <strong>RBAC, Roles, Permissions, Schema e Migrations</strong> permanecem 100% intactos.
          <br>
          • Um <strong>Backup consistente completo do SQLite (VACUUM INTO)</strong> é gerado automaticamente antes de qualquer exclusão em <code>/backups/</code>.
        </p>
      </div>

      ${this.resetResult ? `
        <div class="st-banner ${this.resetResult.success ? 'ok' : 'error'}">
          ${this.resetResult.success ? `
            <strong>✓ Reset Concluído com Sucesso!</strong><br>
            Backup gerado: <code>${this.esc(this.resetResult.backupPath)}</code> (${(this.resetResult.backupSizeBytes / 1024 / 1024).toFixed(2)} MB)<br>
            Executado por: ${this.esc(this.resetResult.executedBy)} em ${new Date(this.resetResult.executedAt).toLocaleString("pt-BR")}<br>
            Todas as tabelas operacionais foram 100% zeradas.
          ` : `
            <strong>Falha na operação:</strong> ${this.esc(this.resetResult.error)}
          `}
        </div>
      ` : ''}

      <div class="st-card">
        <div class="st-card-title">Confirmação de Segurança em Duas Etapas</div>

        <div class="st-row">
          <div class="st-row-label">
            <span>1. Senha do Administrador *</span>
            <small class="st-note">Reautenticação obrigatória validada no servidor.</small>
          </div>
          <div class="st-row-control">
            <input type="password" class="st-ctl" id="st-reset-password" value="${this.esc(this.resetPasswordInput)}" placeholder="Digite sua senha atual de Administrador" autocomplete="current-password" />
          </div>
        </div>

        <div class="st-row">
          <div class="st-row-label">
            <span>2. Frase de Confirmação *</span>
            <small class="st-note">Digite exatamente: <code>EXCLUIR TODOS OS DADOS</code></small>
          </div>
          <div class="st-row-control">
            <input type="text" class="st-ctl" id="st-reset-phrase" value="${this.esc(this.resetPhraseInput)}" placeholder="EXCLUIR TODOS OS DADOS" />
          </div>
        </div>

        <div style="display: flex; justify-content: flex-end; padding-top: 12px; border-top: 1px solid #1C2230;">
          <button class="st-btn bad" id="st-btn-execute-reset" ${canExecute ? '' : 'disabled'} style="background: var(--status-danger-solid, #ef4444); color: #ffffff;">
            <i data-lucide="trash-2"></i> ${this.saving ? 'Executando Reset & Backup…' : 'Executar Reset da Base Operacional'}
          </button>
        </div>
      </div>
    `;
  }

  // ═══════════════════════════════════════════════════════ eventos

  private bind() {
    this.querySelectorAll<HTMLElement>("[data-tab]").forEach(b => b.addEventListener("click", () => this.goTab(b.dataset["tab"]!)));
    this.querySelectorAll<HTMLElement>("[data-goto-tab]").forEach(b => b.addEventListener("click", () => this.goTab(b.dataset["gotoTab"]!)));
    this.querySelector("#st-go-permissions")?.addEventListener("click", () => router.navigate("/administration/people"));

    this.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-sec][data-path]").forEach(el => {
      const handler = () => this.onField(el);
      el.addEventListener("change", handler);
    });
    this.querySelectorAll<HTMLInputElement>("[data-reason]").forEach(el => el.addEventListener("input", () => {
      this.reasons[el.dataset["reason"]!] = el.value;
      // habilita/desabilita os botões que dependem do motivo sem redesenhar (não perder o foco)
      const needs = (el.value ?? "").trim().length >= 5;
      this.querySelector<HTMLButtonElement>("#st-secret-save")?.toggleAttribute("disabled", !(needs && this.secretInput.trim().length >= 8));
      this.querySelector<HTMLButtonElement>("#st-secret-remove")?.toggleAttribute("disabled", !needs);
    }));
    this.querySelectorAll<HTMLElement>("[data-save]").forEach(b => b.addEventListener("click", () => this.save(b.dataset["save"] as EditableDraftSection)));
    this.querySelectorAll<HTMLElement>("[data-discard]").forEach(b => b.addEventListener("click", () => this.discard(b.dataset["discard"] as EditableDraftSection)));

    // numeração
    this.querySelectorAll<HTMLElement>("[data-reset-numbering]").forEach(b => b.addEventListener("click", () => {
      const type = b.dataset["resetNumbering"] as NumberedDocumentType;
      (this.drafts.numbering as SystemSettings["numbering"])[type] = { ...DEFAULT_NUMBERING[type] };
      this.render();
    }));
    this.querySelectorAll<HTMLElement>("[data-build-template]").forEach(b => b.addEventListener("click", () => {
      const type = b.dataset["buildTemplate"] as NumberedDocumentType;
      const numbering = this.drafts.numbering as SystemSettings["numbering"];
      const cfg = numbering[type];
      numbering[type] = { ...cfg, template: buildTemplateFromTokens(["{TIPO}", "{PROJETO}", "{SEQUENCIAL}", "{ANO}"], cfg.separator), resetPerProject: true };
      this.render();
    }));

    // arquivos — extensões
    this.querySelectorAll<HTMLInputElement>("[data-ext]").forEach(el => el.addEventListener("change", () => {
      const files = this.drafts.files as SystemSettings["files"];
      const ext = el.dataset["ext"] as SystemSettings["files"]["allowedImageExtensions"][number];
      files.allowedImageExtensions = el.checked ? [...new Set([...files.allowedImageExtensions, ext])] : files.allowedImageExtensions.filter(e => e !== ext);
      this.render();
    }));

    // importação — aliases
    this.querySelector("#st-alias-add")?.addEventListener("click", () => {
      const imp = this.drafts.import as SystemSettings["import"];
      const first = importTargetFields()[0];
      imp.aliases = [...imp.aliases, { field: first?.field ?? "tag", alias: "" }];
      this.render();
    });
    this.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-alias-index]").forEach(el => el.addEventListener("change", () => {
      const imp = this.drafts.import as SystemSettings["import"];
      const i = Number(el.dataset["aliasIndex"]);
      const part = el.dataset["aliasPart"] as "alias" | "field";
      const row = imp.aliases[i];
      if (row) row[part] = el.value;
      this.render();
    }));
    this.querySelectorAll<HTMLElement>("[data-alias-remove]").forEach(b => b.addEventListener("click", () => {
      const imp = this.drafts.import as SystemSettings["import"];
      imp.aliases = imp.aliases.filter((_, i) => i !== Number(b.dataset["aliasRemove"]));
      this.render();
    }));

    // assinatura — segredo e teste
    const secret = this.querySelector<HTMLInputElement>("#st-secret");
    secret?.addEventListener("input", () => {
      this.secretInput = secret.value;
      this.querySelector<HTMLButtonElement>("#st-secret-save")?.toggleAttribute("disabled", !(secret.value.trim().length >= 8 && (this.reasons["signature"] ?? "").trim().length >= 5));
    });
    this.querySelector("#st-secret-save")?.addEventListener("click", () => this.act(async () => {
      this.view = await this.svc.replaceIcpSecret(this.ctx(), this.secretInput, this.view!.version, this.reasons["signature"] ?? "");
      this.secretInput = "";
      this.drafts.signature = structuredClone(this.view!.settings.signature);
      return "Segredo trocado. O valor foi para o cofre do servidor; aqui fica só a máscara.";
    }));
    this.querySelector("#st-secret-remove")?.addEventListener("click", () => this.act(async () => {
      this.view = await this.svc.removeIcpSecret(this.ctx(), this.view!.version, this.reasons["signature"] ?? "");
      this.drafts.signature = structuredClone(this.view!.settings.signature);
      return "Segredo removido do cofre.";
    }));
    this.querySelector("#st-icp-test")?.addEventListener("click", () => this.act(async () => {
      this.icpTest = await this.svc.testIcpConnection(this.ctx());
      return "";
    }, "Testando a configuração ICP…"));

    // integrações
    this.querySelectorAll<HTMLElement>("[data-int-test]").forEach(b => b.addEventListener("click", () => this.act(async () => {
      const result: DiagnosticResult = await this.svc.testIntegration(this.ctx(), b.dataset["intTest"]);
      this.diagnostics = [{ ...result, label: `Integração — ${result.label}` }];
      this.integrations = await this.svc.listIntegrations(this.ctx());
      return "";
    }, "Testando…")));
    this.querySelectorAll<HTMLElement>("[data-int-toggle]").forEach(b => b.addEventListener("click", () => this.act(async () => {
      this.view = await this.svc.setIntegrationStatus(this.ctx(), "ICP", b.dataset["intToggle"] === "on", this.view!.version, this.reasons["integration"] ?? "");
      if (this.view!.settings.signature) this.drafts.signature = structuredClone(this.view!.settings.signature);
      this.integrations = await this.svc.listIntegrations(this.ctx());
      return b.dataset["intToggle"] === "on" ? "Integração ICP ativada." : "Integração ICP desativada.";
    })));

    // diagnóstico
    this.querySelectorAll<HTMLElement>("[data-diag]").forEach(b => b.addEventListener("click", () => this.runDiag(b.dataset["diag"]!)));
    this.querySelector<HTMLSelectElement>("#st-history-filter")?.addEventListener("change", e => { this.historyFilter = (e.target as HTMLSelectElement).value; this.render(); });

    // Zona de Risco — Reset com Senha + Frase
    const resetPass = this.querySelector<HTMLInputElement>("#st-reset-password");
    resetPass?.addEventListener("input", () => {
      this.resetPasswordInput = resetPass.value;
      const isPhraseValid = this.resetPhraseInput.trim().toUpperCase() === "EXCLUIR TODOS OS DADOS";
      const isPassFilled = this.resetPasswordInput.trim().length > 0;
      this.querySelector<HTMLButtonElement>("#st-btn-execute-reset")?.toggleAttribute("disabled", !(isPhraseValid && isPassFilled && !this.saving));
    });

    const resetPhrase = this.querySelector<HTMLInputElement>("#st-reset-phrase");
    resetPhrase?.addEventListener("input", () => {
      this.resetPhraseInput = resetPhrase.value;
      const isPhraseValid = this.resetPhraseInput.trim().toUpperCase() === "EXCLUIR TODOS OS DADOS";
      const isPassFilled = this.resetPasswordInput.trim().length > 0;
      this.querySelector<HTMLButtonElement>("#st-btn-execute-reset")?.toggleAttribute("disabled", !(isPhraseValid && isPassFilled && !this.saving));
    });

    this.querySelector("#st-btn-execute-reset")?.addEventListener("click", () => this.act(async () => {
      const confirmed = confirm("CONFIRMAÇÃO FINAL:\n\nEsta operação gerará um backup em /backups/ e apagará TODOS os cadastros, relatórios e fotos operacionais.\n\nDeseja prosseguir?");
      if (!confirmed) return "";
      this.saving = true;
      try {
        const result = await this.svc.resetOperationalDatabase(this.ctx(), {
          currentAdminPassword: this.resetPasswordInput,
          confirmationPhrase: this.resetPhraseInput,
          reason: "RESET_PAINEL_CONFIGURACOES"
        });
        this.resetResult = result;
        this.resetPasswordInput = "";
        this.resetPhraseInput = "";
        return "Reset da base operacional concluído com sucesso. Backup consistente gerado.";
      } catch (err: any) {
        this.resetResult = { success: false, error: err?.safeMessage || err?.message || "Falha no reset." };
        throw err;
      } finally {
        this.saving = false;
      }
    }, "Executando backup e limpeza da base operacional…"));
  }

  private onField(el: HTMLInputElement | HTMLSelectElement) {
    const section = el.dataset["sec"] as EditableDraftSection;
    const path = el.dataset["path"]!;
    const kind = el.dataset["kind"];
    let value: unknown = el.value;
    if (kind === "bool") value = (el as HTMLInputElement).checked;
    else if (kind === "int") value = el.value === "" ? 0 : Math.trunc(Number(el.value));
    else if (kind === "float") value = el.value === "" ? 0 : Number(String(el.value).replace(",", "."));
    else if (kind === "nullnum") value = el.value === "" ? null : Math.trunc(Number(el.value));
    else if (kind === "nullstr") value = el.value === "" ? null : el.value;
    else if (kind === "cnpj") value = normalizeCnpj(el.value);
    else if (kind === "list") value = el.value.split(";").map(v => v.trim()).filter(v => v !== "");
    this.setPath(section, path, value);
    this.render();
  }

  private async goTab(id: string) {
    this.tab = id;
    this.notice = null;
    const url = new URL(window.location.href);
    url.searchParams.set("tab", id);
    window.history.replaceState(null, "", url.toString());
    this.render();
    await this.loadTabData();
    this.render();
  }

  private async act(run: () => Promise<string>, busy = "Processando…") {
    this.busy = busy;
    this.notice = null;
    this.render();
    try {
      const message = await run();
      if (message) this.notice = { kind: "ok", text: message };
      this.history = null;
    } catch (e) {
      this.notice = { kind: "error", text: this.messageOf(e, "Não foi possível concluir.") };
    }
    this.busy = "";
    this.render();
  }

  private async runDiag(id: string) {
    await this.act(async () => {
      const ctx = this.ctx();
      if (id === "RELOAD") {
        const r = await this.svc.reloadSettings(ctx);
        clearOperationalSettingsCache();
        await this.load();
        return r.message;
      }
      if (id === "CACHE") {
        const r = await this.svc.clearCache(ctx);
        clearOperationalSettingsCache();
        return `${r.message} O cache desta tela também foi limpo.`;
      }
      // O PDF é gerado no NAVEGADOR (nos dois modos): o teste roda aqui, com o mesmo motor dos relatórios.
      const localPdf = async (): Promise<DiagnosticResult> => {
        const { documentEngine } = await import("../../infrastructure/documents/document-engine.js");
        await import("../../infrastructure/documents/builders/index.js");
        const r = await documentEngine.selfTest();
        return { id: "PDF", label: "PDF Service (neste navegador)", ok: r.ok, status: r.ok ? "OK" : "FALHA", message: r.message, checks: [] };
      };
      if (id === "PDF_LOCAL") { this.diagnostics = [await localPdf()]; return ""; }
      const server: DiagnosticResult[] = await this.svc.runDiagnostic(ctx, id);
      this.diagnostics = id === "ALL" ? [...server.filter(r => r.id !== "PDF"), await localPdf()] : server;
      return "";
    }, "Executando diagnóstico…");
  }

  // ═══════════════════════════════════════════════════════ estilo

  private styles(): string {
    return `
      app-settings-page { display:block; padding:24px 28px; color: var(--ia-fg, #E6E9EF); }
      .st-head { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; margin-bottom:14px; }
      .st-head h1 { font-size:1.35rem; margin:0 0 4px; display:flex; align-items:center; gap:8px; }
      .st-head p { margin:0; font-size:0.82rem; color: var(--ia-fg-soft, #A7B0C0); max-width:90ch; }
      .st-meta { margin-top:6px !important; font-size:0.72rem !important; color: var(--ia-fg-muted, #8A94A6) !important; }
      .st-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:6px; border: 1px solid var(--ia-line, #2B3340); background: var(--ia-bg-panel, #1B212C); color: var(--ia-fg, #E6E9EF); font-size:0.76rem; font-weight:600; cursor:pointer; }
      .st-btn.primary { background: var(--ia-bg-raised, #2B3A47); border-color: var(--ia-line, #3E5263); }
      .st-btn.ghost { background:transparent; }
      .st-btn.sm { padding:5px 10px; font-size:0.7rem; }
      .st-btn[disabled] { opacity:0.45; cursor:not-allowed; }
      .st-icon { background:none; border:none; color: var(--ia-fg-muted, #8A94A6); cursor:pointer; }
      .st-banner { padding:10px 14px; border-radius:6px; margin-bottom:12px; font-size:0.8rem; border:1px solid; }
      .st-banner.ok { background:rgba(16,185,129,0.10); border-color:rgba(16,185,129,0.35); color: var(--ia-success-fg, #a7f3d0); }
      .st-banner.error { background:rgba(239,68,68,0.10); border-color:rgba(239,68,68,0.35); color: var(--ia-danger-fg, #fca5a5); }
      .st-banner.info { background:rgba(125,145,170,0.10); border-color:rgba(125,145,170,0.35); color: var(--ia-fg, #C7D2E0); }
      .st-layout { display:grid; grid-template-columns:240px 1fr; gap:18px; align-items:start; }
      .st-nav { display:flex; flex-direction:column; gap:2px; position:sticky; top:12px; }
      .st-nav-item { display:flex; align-items:center; gap:10px; padding:9px 12px; border-radius:6px; background:none; border:none; color: var(--ia-fg-soft, #A7B0C0); text-align:left; font-size:0.8rem; cursor:pointer; }
      .st-nav-item:hover { background: var(--ia-bg-panel, #151A24); }
      .st-nav-item.active { background: var(--ia-bg-panel, #1B222E); color: var(--ia-fg, #E6E9EF); font-weight:600; box-shadow: inset 2px 0 0 #7D91AA; }
      .st-num { font-size:0.66rem; color: var(--ia-fg-muted, #8A94A6); width:18px; }
      .st-main { min-width:0; display:flex; flex-direction:column; gap:12px; }
      .st-section-head h2 { font-size:1.05rem; margin:0 0 4px; display:flex; align-items:center; gap:8px; }
      .st-section-head p { margin:0; font-size:0.8rem; color: var(--ia-fg-soft, #A7B0C0); line-height:1.5; }
      .st-card { background: var(--ia-bg-panel, #131822); border: 1px solid var(--ia-line, #232A38); border-radius:8px; padding:14px 16px; display:flex; flex-direction:column; gap:10px; }
      .st-card-title { font-size:0.8rem; font-weight:700; letter-spacing:0.03em; text-transform:uppercase; color: var(--ia-fg, #C7CEDA); display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
      .st-row { display:grid; grid-template-columns:minmax(220px, 38%) 1fr; gap:14px; padding:8px 0; border-bottom:1px solid #1C2230; }
      .st-row:last-child { border-bottom:none; }
      .st-row-label { display:flex; flex-direction:column; gap:4px; font-size:0.8rem; }
      .st-row-control { display:flex; flex-direction:column; gap:4px; }
      .st-ctl { background: var(--ia-bg-panel, #0F131A); border: 1px solid var(--ia-line, #2B3340); border-radius:6px; padding:7px 10px; color: var(--ia-fg, #E6E9EF); font-size:0.8rem; width:100%; box-sizing:border-box; }
      .st-ctl[disabled] { opacity:0.6; }
      .st-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(190px, 1fr)); gap:10px; }
      .st-field { display:flex; flex-direction:column; gap:4px; font-size:0.72rem; color: var(--ia-fg-soft, #A7B0C0); }
      .st-field.wide { grid-column: span 2; }
      .st-inline { display:flex; flex-wrap:wrap; gap:14px; align-items:center; }
      .st-toggle { display:inline-flex; align-items:center; gap:6px; font-size:0.78rem; cursor:pointer; }
      .st-radio { display:flex; flex-direction:column; gap:2px; padding:8px 10px; border:1px solid #232A38; border-radius:6px; font-size:0.8rem; }
      .st-radio.off { opacity:0.6; }
      .st-radio small { color: var(--ia-fg-muted, #8A94A6); font-size:0.72rem; margin-left:22px; }
      .st-note { font-size:0.7rem; color: var(--ia-fg-muted, #8A94A6); line-height:1.4; display:block; }
      .st-good { font-size:0.7rem; color: var(--ia-success-fg, #86efac); }
      .st-bad { font-size:0.72rem; color: var(--ia-danger-fg, #fca5a5); }
      .st-muted { color: var(--ia-fg-muted, #8A94A6); font-size:0.74rem; }
      .st-effect { display:inline-flex; align-items:center; gap:4px; font-size:0.62rem; font-weight:700; letter-spacing:0.04em; padding:2px 7px; border-radius:999px; width:fit-content; }
      .st-effect.ok { background:rgba(16,185,129,0.12); color: var(--ia-success-fg, #86efac); }
      .st-effect.reg { background:rgba(217,160,70,0.12); color: var(--ia-warning-fg, #E7C28A); }
      .st-tag { display:inline-block; padding:2px 8px; border-radius:999px; font-size:0.66rem; font-weight:700; background:rgba(148,163,184,0.14); color: var(--ia-fg, #cbd5e1); }
      .st-tag.ok { background:rgba(16,185,129,0.14); color: var(--ia-success-fg, #a7f3d0); }
      .st-tag.warn { background:rgba(217,160,70,0.14); color: var(--ia-warning-fg, #E7C28A); }
      .st-tag.bad { background:rgba(239,68,68,0.14); color: var(--ia-danger-fg, #fca5a5); }
      .st-tag.lock { background:rgba(125,145,170,0.16); color: var(--ia-fg, #C7D2E0); }
      .st-tag.muted { opacity:0.8; }
      .st-table { width:100%; border-collapse:collapse; font-size:0.76rem; }
      .st-table th { text-align:left; padding:7px 8px; border-bottom: 1px solid var(--ia-line, #2B3340); color: var(--ia-fg-muted, #8A94A6); font-size:0.64rem; text-transform:uppercase; letter-spacing:0.05em; }
      .st-table td { padding:7px 8px; border-bottom:1px solid #1C2230; vertical-align:top; }
      .st-table tr.st-off td { opacity:0.6; }
      .st-actions { display:flex; flex-wrap:wrap; gap:6px; }
      .st-savebar { display:flex; justify-content:space-between; align-items:flex-end; gap:12px; padding:12px 16px; background: var(--ia-bg-panel, #10151E); border: 1px solid var(--ia-line, #232A38); border-radius:8px; position:sticky; bottom:8px; }
      .st-savebar.readonly { justify-content:flex-start; align-items:center; color: var(--ia-fg-muted, #8A94A6); font-size:0.78rem; gap:8px; position:static; }
      .st-savebar-actions { display:flex; gap:8px; }
      .st-reason { display:flex; flex-direction:column; gap:4px; font-size:0.7rem; color: var(--ia-fg-soft, #A7B0C0); flex:1; max-width:560px; }
      .st-locked { border:1px solid rgba(125,145,170,0.3); border-radius:8px; padding:12px 14px; background:rgba(125,145,170,0.06); }
      .st-locked-head { display:flex; align-items:center; gap:6px; font-size:0.74rem; font-weight:700; letter-spacing:0.04em; color: var(--ia-fg, #C7D2E0); text-transform:uppercase; margin-bottom:8px; }
      .st-tokens code, .st-token { font-size:0.7rem; background: var(--ia-bg-panel, #0F131A); border: 1px solid var(--ia-line, #2B3340); border-radius:4px; padding:1px 5px; }
      .st-token.legacy { border-style:dashed; opacity:0.8; }
      .st-preview { font-size:0.8rem; }
      .st-preview code { font-size:0.85rem; color: var(--ia-fg, #E6E9EF); background: var(--ia-bg-panel, #0F131A); padding:2px 8px; border-radius:4px; }
      .st-warnline { font-size:0.72rem; color: var(--ia-warning-fg, #E7C28A); display:flex; gap:6px; align-items:flex-start; }
      .st-flow { display:flex; flex-wrap:wrap; gap:6px; align-items:center; }
      .st-flow-step { padding:6px 10px; border: 1px solid var(--ia-line, #2B3340); border-radius:6px; background: var(--ia-bg-panel, #0F131A); font-size:0.74rem; }
      .st-flow-arrow { color: var(--ia-fg-muted, #8A94A6); }
      .st-domain-req { font-size:0.72rem; color: var(--ia-fg-soft, #A7B0C0); display:flex; gap:6px; align-items:flex-start; }
      .st-example p { margin:0; font-size:0.8rem; }
      .st-result { border: 1px solid var(--ia-line, #232A38); border-radius:8px; padding:12px 14px; background: var(--ia-bg-panel, #131822); font-size:0.8rem; }
      .st-result.ok { border-color:rgba(16,185,129,0.35); }
      .st-result.warn { border-color:rgba(217,160,70,0.4); }
      .st-result.bad { border-color:rgba(239,68,68,0.4); }
      .st-result p { margin:6px 0; color: var(--ia-fg, #C7CEDA); }
      .st-kv { display:flex; flex-direction:column; gap:2px; font-size:0.72rem; color: var(--ia-fg-muted, #8A94A6); }
      .st-kv b { font-size:0.85rem; color: var(--ia-fg, #E6E9EF); }
      .st-actions-card { flex-direction:row; flex-wrap:wrap; }
      .st-muted-card { font-size:0.78rem; color: var(--ia-fg-soft, #A7B0C0); }
      .st-val { font-size:0.7rem; word-break:break-all; }
      .st-empty { padding:30px; text-align:center; color: var(--ia-fg-muted, #8A94A6); font-size:0.82rem; }
      @media (max-width: 1100px) { .st-layout { grid-template-columns:1fr; } .st-nav { flex-direction:row; flex-wrap:wrap; position:static; } .st-row { grid-template-columns:1fr; } .st-field.wide { grid-column:auto; } }
    `;
  }
}

if (!customElements.get("app-settings-page")) {
  customElements.define("app-settings-page", AppSettingsPage);
}
